import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { Fragment, Suspense, useEffect } from "react";
import { useFragment, useQueryLoader } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display/Loader";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { getPunchPassSummaryAtom } from "../../../../atoms/selectors/getPunchPassSummaryAtom/getPunchPassSummaryAtom";
import { PunchPassSummary } from "../../../../atoms/types";
import { useGetBookingCharge } from "../../hooks/useGetBookingCharge/useGetBookingCharge";
import { useGetBookingFees } from "../../hooks/useGetBookingFees";
import { BookingChargeItemList } from "./BookingChargeItemList/BookingChargeItemList";
import type { BookingPunchPassSummaryInfo } from "./BookingChargeItemList/BookingChargeItemList";
import { BookingFeeList } from "./RoundListAdditionalBookingFeeList";
import { RoundListAdditionalBookingInfoItemDisplay } from "./RoundListAdditionalBookingInfoItemDisplay";
import { RoundListAdditionalLessonInfoItemDisplayQueryString } from "./RoundListAdditionalLessonInfoItemDisplay";
import { RoundListAdditionalLessonInfoItemDisplay } from "./RoundListAdditionalLessonInfoItemDisplay";
import { RoundListAdditionalBookingInfo_Booking$key } from "./__generated__/RoundListAdditionalBookingInfo_Booking.graphql";
import { RoundListAdditionalLessonInfoItemDisplayQuery } from "./__generated__/RoundListAdditionalLessonInfoItemDisplayQuery.graphql";
import {
  AdditionalBookingInfoItemType,
  AdditionalLessonInfoItemType,
} from "./types";

const additionalLessonInfoItems: AdditionalLessonInfoItemType[] = [
  { type: "INSTRUCTOR" },
  { type: "VENUE" },
  { type: "LESSON_PRICE" },
  { type: "BOOKING_FEE" },
  { type: "RECURRING" },
  { type: "CAPACITY" },
];

type RoundListAdditionalBookingInfoProps = {
  bookingFragmentRef: RoundListAdditionalBookingInfo_Booking$key | null;
  viewMore: boolean;
  setViewMore: (isViewMoreVisible: boolean) => void;
};

type AdditionalBookingInfoConfig = {
  shouldShowPrivateBookings: boolean;
  shouldHidePrices: boolean;
  bookingChargeGroupsLength: number;
  bookingChargeTotal: number;
};

export const getAdditionalBookingInfoItems = ({
  shouldShowPrivateBookings,
  shouldHidePrices,
  bookingChargeGroupsLength,
  bookingChargeTotal,
}: AdditionalBookingInfoConfig): AdditionalBookingInfoItemType[] => {
  const additionalBookingInfoItems: AdditionalBookingInfoItemType[] = [
    { type: "BOOKING_DURATION" },
  ];

  if (!shouldHidePrices) {
    additionalBookingInfoItems.push({ type: "BOOKING_FEE" });
  }

  if (shouldShowPrivateBookings) {
    additionalBookingInfoItems.push({ type: "VENUE" });
  }

  // Show booking charge row whenever booking-level charge groups exist, even if total is $0.
  if (!shouldHidePrices && bookingChargeGroupsLength > 0) {
    additionalBookingInfoItems.push({
      type: "BOOKING_CHARGE",
      total: bookingChargeTotal,
    });
  }

  additionalBookingInfoItems.push({ type: "RECURRING" });
  additionalBookingInfoItems.push({ type: "CAPACITY" });

  return additionalBookingInfoItems;
};

export const RoundListAdditionalBookingInfo = ({
  bookingFragmentRef,
  viewMore,
  setViewMore,
}: RoundListAdditionalBookingInfoProps) => {
  const booking = useFragment(
    RoundListAdditionalBookingInfoFragment,
    bookingFragmentRef
  );
  const punchPassSummaryData: PunchPassSummary | null = useAtomValue(
    getPunchPassSummaryAtom(booking?.id ?? null)
  );

  const isLesson = booking?.__typename === "LessonBooking";

  const [queryRef, loadQuery] =
    useQueryLoader<RoundListAdditionalLessonInfoItemDisplayQuery>(
      RoundListAdditionalLessonInfoItemDisplayQueryString
    );

  const punchPassSummary: BookingPunchPassSummaryInfo = {
    hasValidPunchPass: punchPassSummaryData?.hasValidPunchPass ?? false,
    hasPunchPassApplied: punchPassSummaryData?.hasPunchPassApplied ?? false,
    validPunchPassCount: punchPassSummaryData?.validPunchPassCount ?? 0,
    invalidPunchPassCount: punchPassSummaryData?.invalidPunchPassCount ?? 0,
    totalPunchPassCount: punchPassSummaryData?.totalPunchPassCount ?? 0,
  };

  useEffect(() => {
    if (
      booking?.id &&
      booking?.durationMins &&
      booking?.rounds?.count &&
      isLesson &&
      booking?.lessonType?.id
    ) {
      loadQuery(
        {
          bookingId: booking?.id,
          duration: booking?.durationMins,
          groupSize: booking?.rounds?.count,
          lessonTypeId: booking.lessonType.id,
        },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [
    booking?.id,
    booking?.durationMins,
    booking?.rounds?.count,
    booking?.lessonType?.id,
    loadQuery,
    isLesson,
  ]);

  const { bookingFees } = useGetBookingFees({
    bookingFragmentRef: booking ?? null,
  });

  const { bookingChargeGroups, bookingChargeTotal } = useGetBookingCharge({
    bookingFragmentRef: booking ?? null,
  });

  const clubSupportsPrivateBookings =
    booking?.startSlot?.agenda?.facility?.club?.supportsPrivate;

  const isDurationBasedFacility =
    booking?.startSlot?.agenda?.facility?.type &&
    booking?.startSlot?.agenda?.facility.type !== "GOLF_COURSE";

  const shouldShowPrivateBookings =
    isDurationBasedFacility && clubSupportsPrivateBookings;
  const shouldHidePrices = !!clubSupportsPrivateBookings;
  const shouldShowBookingFees = Boolean(
    !shouldHidePrices && bookingFees?.length && bookingFees?.length > 0
  );

  /**
   * Which Items to include?
   *
   * TODO: This should probably be it's own tested hook.
   * We should also for sure not have so much logic in that display
   * component where it's responsible for knowing everything about
   * every possible display item...
   */
  const additionalBookingInfoItems = getAdditionalBookingInfoItems({
    shouldShowPrivateBookings: !!shouldShowPrivateBookings,
    shouldHidePrices,
    bookingChargeGroupsLength: bookingChargeGroups.length,
    bookingChargeTotal,
  });

  return (
    <Box gap={1} paddingVertical={2}>
      {isLesson
        ? additionalLessonInfoItems.map((item) => (
            <Fragment key={item.type}>
              <Suspense fallback={<Loader />}>
                {queryRef ? (
                  <RoundListAdditionalLessonInfoItemDisplay
                    bookingFragmentRef={booking ?? null}
                    item={item}
                    lessonPriceQueryRef={queryRef}
                  />
                ) : null}
              </Suspense>
              {item.type === "BOOKING_FEE" && shouldShowBookingFees ? (
                <BookingFeeList bookingFees={bookingFees} viewMore={viewMore} />
              ) : null}
            </Fragment>
          ))
        : additionalBookingInfoItems.map((item) => (
            <Box key={item.type}>
              <RoundListAdditionalBookingInfoItemDisplay
                bookingRef={booking ?? null}
                item={item}
                onToggleViewMore={
                  item.type === "BOOKING_CHARGE" &&
                  (bookingChargeGroups.length ||
                    punchPassSummary.totalPunchPassCount > 0)
                    ? () => setViewMore(!viewMore)
                    : undefined
                }
              />
              {item.type === "BOOKING_FEE" && shouldShowBookingFees ? (
                <BookingFeeList bookingFees={bookingFees} viewMore={viewMore} />
              ) : null}
              {item.type === "BOOKING_CHARGE" ? (
                <BookingChargeItemList
                  bookingChargeGroups={bookingChargeGroups}
                  viewMore={viewMore}
                  punchPassInfo={punchPassSummary}
                  bookingFragmentRef={booking ?? null}
                />
              ) : null}
            </Box>
          ))}
    </Box>
  );
};

const RoundListAdditionalBookingInfoFragment = graphql`
  fragment RoundListAdditionalBookingInfo_Booking on Booking {
    ... on LessonBooking {
      durationMins
      lessonType {
        id
      }
    }
    rounds {
      count
    }
    startSlot {
      __typename
      agenda {
        facility {
          type
          club {
            supportsPrivate
          }
        }
      }
    }
    id
    __typename
    ...RoundListAdditionalBookingInfoItemDisplay_Booking
    ...useGetBookingFees_Booking
    ...useGetBookingCharge_Booking
    ...RoundListAdditionalLessonInfoItemDisplay_Booking
    ...useGetSummaryCalculations_Booking
    ...PunchPassSummary_Booking
  }
`;
